import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/apiClient";
import { useBasket } from "@/lib/basket";
import { BrandMark } from "@/components/BrandMark";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { ArrowLeft, Trash2, UserPlus, CalendarDays, Loader2, ShoppingBasket, X, Tag } from "lucide-react";
import { fmtDate, fmtTime } from "@/lib/helpers";

const toPayload = (items) => items.map((i) => ({ course_id: i.course_id, session_id: i.session?.id || null, learners: i.learners }));

function LearnerAdder({ onAdd, testid }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const add = () => {
    if (!name.trim() || !email.trim()) return toast.error("Enter the learner's name and email");
    onAdd({ name: name.trim(), email: email.trim() });
    setName(""); setEmail("");
  };
  return (
    <div className="grid sm:grid-cols-[1fr_1fr_auto] gap-2 mt-3">
      <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Full Learners Name (for Certificate)" data-testid={`${testid}-name-input`} />
      <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email (for Course Links and Certificate)" data-testid={`${testid}-email-input`} />
      <Button variant="outline" onClick={add} data-testid={`${testid}-add-button`}><UserPlus className="h-4 w-4 mr-1.5" />Add learner</Button>
    </div>
  );
}

function BasketItem({ item, idx, onChange, onRemove }) {
  const setLearners = (learners) => onChange({ ...item, learners });
  return (
    <Card className="p-5" data-testid={`basket-item-${idx}`}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="font-heading font-semibold">{item.title}</h3>
          {item.session && (
            <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1.5"><CalendarDays className="h-3.5 w-3.5" />{fmtDate(item.session.start)} · {fmtTime(item.session.start)}{item.session.location ? ` · ${item.session.location}` : ""}</p>
          )}
          <p className="text-sm mt-1">£{item.price.toFixed(2)} ex VAT per learner</p>
        </div>
        <Button variant="ghost" size="sm" onClick={onRemove} data-testid={`basket-item-${idx}-remove`}><Trash2 className="h-4 w-4" /></Button>
      </div>
      <div className="mt-3 space-y-1.5">
        {item.learners.map((l, j) => (
          <div key={j} className="flex items-center justify-between rounded-md bg-accent/40 px-3 py-2 text-sm" data-testid={`basket-item-${idx}-learner-${j}`}>
            <span><span className="font-medium">{l.name}</span> <span className="text-muted-foreground">· {l.email}</span></span>
            <button onClick={() => setLearners(item.learners.filter((_, k) => k !== j))} data-testid={`basket-item-${idx}-learner-${j}-remove`}><X className="h-4 w-4 text-muted-foreground" /></button>
          </div>
        ))}
      </div>
      <LearnerAdder testid={`basket-item-${idx}-learner`} onAdd={(l) => setLearners([...item.learners, l])} />
    </Card>
  );
}

function Totals({ quote }) {
  if (!quote) return <div className="flex justify-center py-4"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div>;
  const row = (k, v, t) => <div className="flex justify-between"><span className="text-muted-foreground">{k}</span><span data-testid={t}>{v}</span></div>;
  return (
    <div className="space-y-1 text-sm" data-testid="basket-totals">
      {row(`Places`, quote.places, "basket-places")}
      {row("Subtotal (ex VAT)", `£${quote.subtotal.toFixed(2)}`, "basket-subtotal")}
      {quote.discount > 0 && row(`Discount (${quote.promo_code})`, `-£${quote.discount.toFixed(2)}`, "basket-discount")}
      {row("VAT (20%)", `£${quote.vat.toFixed(2)}`, "basket-vat")}
      <div className="flex justify-between font-semibold pt-1 border-t border-border"><span>Total to pay</span><span data-testid="basket-total">£{quote.total.toFixed(2)}</span></div>
    </div>
  );
}

export default function Basket() {
  const { items, save } = useBasket();
  const [quote, setQuote] = useState(null);
  const [promoInput, setPromoInput] = useState("");
  const [promo, setPromo] = useState("");
  const [buyer, setBuyer] = useState({ name: "", email: "", confirm: "" });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!items.length) { setQuote(null); return; }
    api.post("/basket/quote", { items: toPayload(items), promo_code: promo || null })
      .then(({ data }) => {
        setQuote(data);
        if (data.promo_invalid) { toast.error("Invalid or inactive code"); setPromo(""); }
      })
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)));
  }, [items, promo]);

  const mismatch = buyer.confirm.trim() !== "" && buyer.confirm.trim().toLowerCase() !== buyer.email.trim().toLowerCase();
  const checkout = async () => {
    if (!buyer.name.trim() || !buyer.email.trim()) return toast.error("Please enter your name and email as the buyer");
    if (buyer.confirm.trim().toLowerCase() !== buyer.email.trim().toLowerCase()) return toast.error("Your email addresses don't match");
    setLoading(true);
    try {
      const { data } = await api.post("/basket/checkout", {
        items: toPayload(items), promo_code: promo || null, buyer_name: buyer.name, buyer_email: buyer.email,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="h-16 border-b border-border bg-white/90 backdrop-blur-md sticky top-0 z-20">
        <div className="max-w-5xl mx-auto h-full flex items-center justify-between px-4 sm:px-6">
          <BrandMark />
          <Link to="/shop" data-testid="basket-back-shop"><Button variant="outline" size="sm"><ArrowLeft className="h-4 w-4 mr-1.5" />Continue shopping</Button></Link>
        </div>
      </header>
      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-10">
        <h1 className="font-heading text-3xl font-bold tracking-tight flex items-center gap-2"><ShoppingBasket className="h-7 w-7 text-primary" />Your basket</h1>
        {!items.length ? (
          <div className="text-center py-20" data-testid="basket-empty">
            <p className="text-muted-foreground">Your basket is empty.</p>
            <Link to="/shop"><Button className="mt-4">Browse courses</Button></Link>
          </div>
        ) : (
          <div className="grid lg:grid-cols-[1fr_360px] gap-6 mt-6">
            <div className="space-y-4">
              {items.map((it, i) => (
                <BasketItem key={it.key} item={it} idx={i}
                  onChange={(n) => save(items.map((x) => (x.key === it.key ? n : x)))}
                  onRemove={() => save(items.filter((x) => x.key !== it.key))} />
              ))}
            </div>
            <Card className="p-5 h-fit space-y-4 lg:sticky lg:top-24">
              <h2 className="font-heading font-semibold">Buyer details</h2>
              <p className="text-xs text-muted-foreground -mt-2">Your receipt and invoice will be sent here. Each learner gets their own access link.</p>
              <div><Label>Your full name</Label><Input value={buyer.name} onChange={(e) => setBuyer({ ...buyer, name: e.target.value })} className="mt-1.5" data-testid="basket-buyer-name" /></div>
              <div><Label>Your email</Label><Input type="email" value={buyer.email} onChange={(e) => setBuyer({ ...buyer, email: e.target.value })} className="mt-1.5" data-testid="basket-buyer-email" /></div>
              <div>
                <Label>Confirm your email</Label>
                <Input type="email" value={buyer.confirm} onPaste={(e) => e.preventDefault()} onChange={(e) => setBuyer({ ...buyer, confirm: e.target.value })} className={`mt-1.5 ${mismatch ? "border-red-500" : ""}`} data-testid="basket-buyer-confirm" />
                {mismatch && <p className="text-xs text-red-600 mt-1" data-testid="basket-email-mismatch">Email addresses don't match</p>}
              </div>
              <div>
                <Label>Discount code</Label>
                {promo && quote?.promo_code ? (
                  <div className="flex items-center justify-between mt-1.5 rounded-lg border border-emerald-300 bg-emerald-50 p-2.5 text-sm text-emerald-700">
                    <span className="flex items-center gap-1.5"><Tag className="h-4 w-4" />{quote.promo_code} · {quote.promo_message}</span>
                    <button onClick={() => { setPromo(""); setPromoInput(""); }} data-testid="basket-remove-promo"><X className="h-4 w-4" /></button>
                  </div>
                ) : (
                  <div className="flex gap-2 mt-1.5">
                    <Input value={promoInput} onChange={(e) => setPromoInput(e.target.value.toUpperCase())} placeholder="Enter code" data-testid="basket-promo-input" />
                    <Button variant="outline" onClick={() => setPromo(promoInput.trim())} data-testid="basket-apply-promo">Apply</Button>
                  </div>
                )}
              </div>
              <Totals quote={quote} />
              <Button className="w-full" onClick={checkout} disabled={loading || !quote} data-testid="basket-checkout-button">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : quote && quote.total <= 0 ? "Confirm free booking" : `Pay £${quote ? quote.total.toFixed(2) : "0.00"} inc VAT`}
              </Button>
            </Card>
          </div>
        )}
      </main>
    </div>
  );
}
